import { useCallback, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Helmet } from "@dr.pogodin/react-helmet";
import { Bell, ClipboardList, Users, LogOut, LayoutDashboard, ShieldCheck, Building2, ShoppingBag, Megaphone, FileText, Gift, TrendingUp, Image as ImageIcon, Store, BarChart3 } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { Logo } from "@/components/lembra/Logo";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useProfile, useUnreadNotificationCount } from "@/hooks/queries";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const navItems = [
  { title: "Overview", url: "/admin", icon: LayoutDashboard },
  { title: "Applications", url: "/admin/applications", icon: ClipboardList },
  { title: "Partners", url: "/admin/partners", icon: Users },
  { title: "Companies", url: "/admin/companies", icon: Building2 },
  { title: "Orders", url: "/admin/orders", icon: ShoppingBag },
  // Sprint 1.7j, focused triage for chat/browse-originated orders,
  // separate nav slot from the legacy Orders so ops can scan the
  // new flow without scrolling past historic cake orders.
  { title: "Gift Requests", url: "/admin/gift-requests", icon: Gift },
  // Sprint 1.7l, funnel attribution dashboard, reads from
  // interaction_captures. Sits adjacent to ops surfaces because
  // both inform the same operational decisions.
  { title: "Funnel", url: "/admin/funnel", icon: TrendingUp },
  // Sprint 2.1b, per-vendor performance rollup. Sits next to Funnel
  // because both are measurement surfaces; differs from Partners
  // (which is partner CRUD/admin) by being analytics-focused.
  { title: "Vendors", url: "/admin/vendors", icon: Store },
  // Sprint 1.7m, catalogue imagery curation. Lives next to Funnel
  // because both are content/measurement tools rather than triage.
  { title: "Catalogue", url: "/admin/catalogue-imagery", icon: ImageIcon },
  // MS241: price-tier gap report. Surfaces which price bands
  // (≤$10, $10-20, $20-30, etc.) have no SKUs so we know which
  // tiers to source new vendors for vs. ask existing vendors to
  // add cheaper add-ons.
  { title: "Tiers", url: "/admin/catalogue-tiers", icon: BarChart3 },
  { title: "Notifications", url: "/admin/notifications", icon: Bell },
  { title: "Notices", url: "/admin/notices", icon: Megaphone },
  { title: "Blog", url: "/admin/blog", icon: FileText },
];

function AdminSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Unread badge, same hook + invalidation pattern AppLayout uses for the
  // customer-side bell. Refreshed on route change and when the Notifications
  // page (or any other component) fires "lembra:notifications-read".
  const unreadQuery = useUnreadNotificationCount(user?.id);
  const unreadCount = unreadQuery.data ?? 0;

  const invalidateUnread = useCallback(() => {
    if (!user?.id) return;
    queryClient.invalidateQueries({ queryKey: ["notifications", user.id, "unread-count"] });
  }, [queryClient, user?.id]);

  useEffect(() => {
    invalidateUnread();
  }, [location.pathname, invalidateUnread]);

  useEffect(() => {
    const handler = () => invalidateUnread();
    window.addEventListener("lembra:notifications-read", handler);
    return () => window.removeEventListener("lembra:notifications-read", handler);
  }, [invalidateUnread]);

  return (
    <Sidebar collapsible="icon" className="border-none">
      <div className="flex h-full flex-col bg-navy overflow-y-auto">
        <div className={`flex flex-col items-center py-6 ${collapsed ? "px-0" : "px-4"}`}>
          {!collapsed && (
            <div className="w-full">
              <Logo size="md" onDark />
              <div className="flex items-center justify-center gap-1.5 mt-2">
                <ShieldCheck className="h-3.5 w-3.5 text-coral" />
                <p className="text-coral text-xs font-bold uppercase tracking-widest">Admin</p>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-coral">
              <ShieldCheck className="h-4 w-4 text-white" />
            </div>
          )}
        </div>

        <SidebarContent className="bg-navy px-1">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => {
                  // `Overview` lives at the bare /admin path so any other
                  // /admin/* route would also "startsWith('/admin')". Use
                  // exact match for that one entry to keep its highlight
                  // from sticking on every sub-page.
                  const isActive = item.url === "/admin"
                    ? location.pathname === "/admin"
                    : location.pathname.startsWith(item.url);
                  const isNotif = item.title === "Notifications";
                  const linkContent = (
                    <NavLink
                      to={item.url}
                      end={item.url === "/admin"}
                      className={`relative flex items-center rounded-lg text-sm font-medium transition-all duration-200 ${
                        collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"
                      } ${
                        isActive ? "bg-butter text-navy" : "text-white/80 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {!collapsed && <span>{item.title}</span>}
                      {isNotif && unreadCount > 0 && (
                        <span
                          className={`flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-coral px-1 text-[10px] font-bold text-white animate-pulse ${
                            collapsed
                              ? "absolute -top-1 -right-1"
                              : "absolute right-2 top-1/2 -translate-y-1/2"
                          }`}
                        >
                          {unreadCount > 99 ? "99+" : unreadCount}
                        </span>
                      )}
                    </NavLink>
                  );
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        {collapsed ? (
                          <Tooltip>
                            <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                            <TooltipContent side="right" className="bg-navy text-white border-navy">
                              {item.title}
                            </TooltipContent>
                          </Tooltip>
                        ) : linkContent}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </div>
    </Sidebar>
  );
}

function AdminAvatarMenu() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const profileQuery = useProfile();
  const displayName = profileQuery.data?.full_name || user?.email || "";
  const initials = displayName.charAt(0).toUpperCase() || "A";

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex h-9 w-9 items-center justify-center rounded-full bg-coral text-white text-sm font-bold hover:bg-coral/90 transition-colors focus:outline-none">
          {initials}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-coral focus:text-coral">
          <LogOut className="mr-2 h-4 w-4" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AdminLayout() {
  return (
    <SidebarProvider>
      {/* MS408: blanket noindex on all /admin/* routes. These pages are
          behind auth in practice, but belt-and-braces prevents any edge
          case (cached session, misconfigured proxy) from letting
          Googlebot surface internal ops URLs. Deepest-wins Helmet dedup
          means per-page <PageMeta> blocks override if needed. */}
      <Helmet>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 flex items-center justify-between border-b border-charcoal/10 px-4 bg-white sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <SidebarTrigger />
              <span className="text-xs font-semibold text-charcoal/50 uppercase tracking-wider">Lembra Admin</span>
            </div>
            <AdminAvatarMenu />
          </header>
          <main className="flex-1 bg-cream p-6 md:p-10">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
